/**
 * TopSheet - Reusable slide-down panel that appears below the top toolbar.
 * User-resizable via drag handle at the bottom edge.
 * Styled to match the NPC dialog box visual language.
 */
export default class TopSheet {
  static MIN_HEIGHT = 100;
  static DEFAULT_HEIGHT = 300;

  constructor(options = {}) {
    this.id = options.id || 'top-sheet';
    this._isOpen = false;
    this._height = TopSheet.DEFAULT_HEIGHT;
    this._resizing = false;
    this._resizeStartY = 0;
    this._resizeStartHeight = 0;

    this._createDOM();
    this._setupEvents();
  }

  _createDOM() {
    // Sheet container (no backdrop — game stays interactive)
    this.sheet = document.createElement('div');
    this.sheet.className = 'top-sheet';
    this.sheet.id = this.id;
    this.sheet.style.display = 'none';

    // Header
    const header = document.createElement('div');
    header.className = 'top-sheet-header';

    this.titleEl = document.createElement('span');
    this.titleEl.className = 'top-sheet-title';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'top-sheet-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', () => this.close());

    header.appendChild(this.titleEl);
    header.appendChild(closeBtn);

    // Scrollable content
    this.content = document.createElement('div');
    this.content.className = 'top-sheet-content';

    // Resize handle at bottom
    this.handle = document.createElement('div');
    this.handle.className = 'top-sheet-handle';
    const handleBar = document.createElement('div');
    handleBar.className = 'top-sheet-handle-bar';
    this.handle.appendChild(handleBar);

    this.sheet.appendChild(header);
    this.sheet.appendChild(this.content);
    this.sheet.appendChild(this.handle);

    document.body.appendChild(this.sheet);
  }

  _setupEvents() {
    // Resize handle - pointer events for mouse + touch
    this.handle.addEventListener('pointerdown', (e) => this._onResizeStart(e));
    this._onResizeMoveBound = (e) => this._onResizeMove(e);
    this._onResizeEndBound = (e) => this._onResizeEnd(e);
  }

  _onResizeStart(e) {
    e.preventDefault();
    this._resizing = true;
    this._resizeStartY = e.clientY;
    this._resizeStartHeight = this._height;
    this.handle.setPointerCapture(e.pointerId);
    document.addEventListener('pointermove', this._onResizeMoveBound);
    document.addEventListener('pointerup', this._onResizeEndBound);
    this.sheet.classList.add('resizing');
  }

  _onResizeMove(e) {
    if (!this._resizing) return;
    const delta = e.clientY - this._resizeStartY;
    const newHeight = Math.max(TopSheet.MIN_HEIGHT, this._resizeStartHeight + delta);
    const maxHeight = window.innerHeight * 0.7;
    this._height = Math.min(newHeight, maxHeight);
    this._applyHeight();
  }

  _onResizeEnd(e) {
    if (!this._resizing) return;
    this._resizing = false;
    document.removeEventListener('pointermove', this._onResizeMoveBound);
    document.removeEventListener('pointerup', this._onResizeEndBound);
    this.sheet.classList.remove('resizing');

    // If dragged up past minimum, dismiss
    if (this._height <= TopSheet.MIN_HEIGHT) {
      this.close();
    }
  }

  _applyHeight() {
    this.content.style.maxHeight = `${this._height - 80}px`; // 80px for header + handle
  }

  /**
   * Open the sheet with a title
   * @param {string} title
   */
  open(title = '') {
    if (this._isOpen) return;
    this._isOpen = true;
    this.titleEl.textContent = title;
    this._applyHeight();
    this.sheet.style.display = 'flex';
    window.game?.audioSystem?.audio?.playUiOpen();

    // Trigger slide-down animation
    requestAnimationFrame(() => {
      this.sheet.classList.add('open');
    });
  }

  /**
   * Close the sheet
   */
  close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    window.game?.audioSystem?.audio?.playUiClose();
    this.sheet.classList.remove('open');

    // Wait for animation to finish
    setTimeout(() => {
      if (!this._isOpen) {
        this.sheet.style.display = 'none';
      }
    }, 300);
  }

  /**
   * Check if the sheet is open
   * @returns {boolean}
   */
  isOpen() {
    return this._isOpen;
  }

  /**
   * Get the scrollable content container
   * @returns {HTMLElement}
   */
  getContentContainer() {
    return this.content;
  }

  /**
   * Destroy and remove from DOM
   */
  destroy() {
    this.sheet.remove();
  }
}
